import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  PencilSimpleIcon,
  CheckIcon,
  XIcon,
  WarningIcon,
  KeyIcon,
  LockKeyIcon,
} from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import {
  DangerButton,
  ErrorBanner,
  INPUT_CLS,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  Field,
} from "../../components/crm/Form";
import TeamRoleBadge from "../../components/team/TeamRoleBadge";
import TagInput from "../../components/team/TagInput";
import SchedulingPrefsCard from "../../components/team/SchedulingPrefsCard";
import TempPasswordModal from "../../components/team/TempPasswordModal";
import { useAuth } from "../../contexts/AuthContext";
import { formatApiError } from "../../lib/api";
import {
  deactivateTeamMember,
  getInitials,
  getTeamMember,
  isoDate,
  reactivateTeamMember,
  relativeTime,
  resetTeamMemberPassword,
  ROLE_OPTIONS,
  updateTeamMember,
  changeOwnPassword,
} from "../../lib/team";

export default function TeamMemberDetail() {
  const { userId } = useParams();
  const { user: viewer } = useAuth();
  const navigate = useNavigate();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tempPw, setTempPw] = useState(null);

  const isOwner = viewer?.role === "owner";
  const isOps = viewer?.role === "ops";
  const isOffice = isOwner || isOps;
  const isSelf = viewer?.id === userId;

  // Office can edit name/phone/certs/regions/sched. Techs can only view (own only).
  const canEditBasics = isOffice;
  const canEditNotes = isOwner;
  const canManage = isOwner;

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await getTeamMember(userId);
      setMember(data);
    } catch (e) {
      if (e?.response?.status === 404) setErr("Team member not found.");
      else if (e?.response?.status === 403) setErr("You don't have access to this profile.");
      else setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Layout>
        <DetailSkeleton />
      </Layout>
    );
  }

  if (err || !member) {
    return (
      <Layout>
        <ErrorBanner message={err} />
        <button
          type="button"
          onClick={() => navigate("/team")}
          className="text-sm text-slate-700 hover:text-slate-900 font-semibold"
        >
          ← Back to team
        </button>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title={member.name}
          subtitle={member.email}
        />

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Left column ----------------------------------------------- */}
          <div className="space-y-4">
            <ProfileCard member={member} />
            <TagsCard
              member={member}
              field="certifications"
              label="Certifications"
              placeholder="e.g. NABCEP PV Associate"
              canEdit={canEditBasics}
              onSaved={load}
            />
            <TagsCard
              member={member}
              field="service_regions"
              label="Service regions"
              placeholder="e.g. Salt Lake"
              canEdit={canEditBasics}
              onSaved={load}
            />
            <SchedulingPrefsCard
              member={member}
              canEdit={canEditBasics}
              onSaved={load}
            />
            {canEditNotes && (
              <NotesCard member={member} onSaved={load} />
            )}
          </div>

          {/* Right sidebar -------------------------------------------- */}
          <aside className="space-y-4">
            <QuickInfoCard member={member} />
            {isSelf && <ChangePasswordCard />}
            <ContactEditCard
              member={member}
              canEdit={canEditBasics}
              onSaved={load}
            />
            {canManage && (
              <RoleCard member={member} onSaved={load} />
            )}
            {canManage && !isSelf && (
              <AccountActionsCard
                member={member}
                onSaved={load}
                onResetPassword={(pw) =>
                  setTempPw({ member, password: pw })
                }
              />
            )}
          </aside>
        </div>
      </div>

      <TempPasswordModal
        open={Boolean(tempPw)}
        onClose={() => setTempPw(null)}
        email={tempPw?.member?.email}
        memberName={tempPw?.member?.name}
        tempPassword={tempPw?.password}
        variant="reset"
      />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Profile card — initials + identity + status pill
// ---------------------------------------------------------------------------

function ProfileCard({ member }) {
  return (
    <section
      data-testid="team-profile-card"
      className="bg-white border border-slate-200 rounded-md p-5 flex items-center gap-5"
    >
      <div
        className="h-16 w-16 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xl font-bold flex-none"
        aria-hidden="true"
      >
        {getInitials(member.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-heading text-xl font-bold text-slate-900 truncate">
          {member.name}
        </div>
        <div className="text-sm text-slate-600 truncate">{member.email}</div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <TeamRoleBadge role={member.role} />
          {member.is_active ? (
            <span
              data-testid="team-profile-status-active"
              className="inline-flex items-center rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800"
            >
              Active
            </span>
          ) : (
            <span
              data-testid="team-profile-status-inactive"
              className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500"
            >
              Inactive
            </span>
          )}
          {member.phone && (
            <span className="font-data text-xs text-slate-700">
              {member.phone}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TagsCard — wrap-and-reuse for certifications + service_regions
// ---------------------------------------------------------------------------

function TagsCard({ member, field, label, placeholder, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(member[field] || []);
  const [busy, setBusy] = useState(false);

  const begin = () => {
    setDraft(member[field] || []);
    setEditing(true);
  };
  const cancel = () => setEditing(false);

  const save = async () => {
    setBusy(true);
    try {
      await updateTeamMember(member.id, { [field]: draft });
      toast.success(`${label} saved`);
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const values = member[field] || [];

  return (
    <section
      data-testid={`team-${field}-card`}
      className="bg-white border border-slate-200 rounded-md p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading text-sm font-semibold text-slate-900">
          {label}
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={begin}
            data-testid={`team-${field}-edit`}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label={`Edit ${label}`}
          >
            <PencilSimpleIcon size={14} />
          </button>
        )}
      </div>
      {!editing && (
        <div className="flex flex-wrap gap-1.5">
          {values.length === 0 ? (
            <span className="text-xs text-slate-400 italic">None set</span>
          ) : (
            values.map((t, idx) => (
              <span
                key={`${t}-${idx}`}
                data-testid={`team-${field}-pill-${idx}`}
                className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs text-slate-800"
              >
                {t}
              </span>
            ))
          )}
        </div>
      )}
      {editing && (
        <div className="space-y-3">
          <TagInput
            value={draft}
            onChange={setDraft}
            placeholder={placeholder}
            testidPrefix={`team-${field}-edit-tag`}
          />
          <div className="flex justify-end gap-2">
            <SecondaryButton type="button" onClick={cancel} disabled={busy}>
              <XIcon size={14} /> Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={save}
              disabled={busy}
              data-testid={`team-${field}-save`}
            >
              <CheckIcon size={14} /> {busy ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// NotesCard — owner only, internal notes
// ---------------------------------------------------------------------------

function NotesCard({ member, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(member.notes || "");
  const [busy, setBusy] = useState(false);

  const begin = () => {
    setDraft(member.notes || "");
    setEditing(true);
  };
  const cancel = () => setEditing(false);

  const save = async () => {
    setBusy(true);
    try {
      await updateTeamMember(member.id, { notes: draft.trim() || null });
      toast.success("Notes saved");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="team-notes-card"
      className="bg-white border border-amber-200 rounded-md p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading text-sm font-semibold text-slate-900">
          Internal notes
          <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
            Owner only
          </span>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={begin}
            data-testid="team-notes-edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Edit notes"
          >
            <PencilSimpleIcon size={14} />
          </button>
        )}
      </div>
      {!editing && (
        <div
          data-testid="team-notes-display"
          className="text-sm text-slate-800 whitespace-pre-wrap"
        >
          {member.notes || (
            <span className="text-slate-400 italic">No notes</span>
          )}
        </div>
      )}
      {editing && (
        <div className="space-y-2">
          <TextArea
            value={draft}
            onChange={setDraft}
            rows={4}
            maxLength={1000}
            data-testid="team-notes-input"
          />
          <div className="text-[10px] text-slate-500">
            {draft.length}/1000
          </div>
          <div className="flex justify-end gap-2">
            <SecondaryButton type="button" onClick={cancel} disabled={busy}>
              <XIcon size={14} /> Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="team-notes-save"
            >
              <CheckIcon size={14} /> {busy ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// QuickInfoCard — sidebar
// ---------------------------------------------------------------------------

function QuickInfoCard({ member }) {
  return (
    <section
      data-testid="team-quick-info"
      className="bg-white border border-slate-200 rounded-md p-4 space-y-2 text-xs"
    >
      <Row label="Member since" value={isoDate(member.created_at)} />
      <Row
        label="Last updated"
        value={
          <span title={member.updated_at}>{relativeTime(member.updated_at)}</span>
        }
      />
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContactEditCard — name + phone (basics editable by office)
// ---------------------------------------------------------------------------

function ContactEditCard({ member, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(member.name || "");
  const [phone, setPhone] = useState(member.phone || "");
  const [busy, setBusy] = useState(false);

  const begin = () => {
    setName(member.name || "");
    setPhone(member.phone || "");
    setEditing(true);
  };
  const cancel = () => setEditing(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: name.trim(), phone: phone.trim() || null };
      await updateTeamMember(member.id, payload);
      toast.success("Contact saved");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="team-contact-card"
      className="bg-white border border-slate-200 rounded-md p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
          Contact
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={begin}
            data-testid="team-contact-edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Edit contact"
          >
            <PencilSimpleIcon size={14} />
          </button>
        )}
      </div>
      {!editing && (
        <dl className="space-y-2 text-sm">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              Name
            </dt>
            <dd className="mt-0.5 text-slate-900">{member.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              Phone
            </dt>
            <dd className="mt-0.5 font-data text-slate-900">
              {member.phone || <span className="text-slate-400">—</span>}
            </dd>
          </div>
        </dl>
      )}
      {editing && (
        <div className="space-y-3">
          <Field label="Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="team-contact-name-input"
              maxLength={120}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Phone (E.164)">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+14155551234"
              data-testid="team-contact-phone-input"
              className={INPUT_CLS}
            />
          </Field>
          <div className="flex justify-end gap-2">
            <SecondaryButton type="button" onClick={cancel} disabled={busy}>
              <XIcon size={14} /> Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={save}
              disabled={busy || !name.trim()}
              data-testid="team-contact-save"
            >
              <CheckIcon size={14} /> {busy ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// RoleCard — owner only. Confirm step before changing role.
// ---------------------------------------------------------------------------

function RoleCard({ member, onSaved }) {
  const [draft, setDraft] = useState(member.role);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const dirty = draft !== member.role;

  const submit = async () => {
    setBusy(true);
    try {
      await updateTeamMember(member.id, { role: draft });
      toast.success("Role updated");
      setConfirming(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="team-role-card"
      className="bg-white border border-slate-200 rounded-md p-4"
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
        Role
      </div>
      <Select
        value={draft}
        onChange={(v) => setDraft(v || member.role)}
        options={ROLE_OPTIONS}
        placeholder=""
        data-testid="team-role-select"
      />
      {dirty && !confirming && (
        <div className="mt-3">
          <PrimaryButton
            type="button"
            onClick={() => setConfirming(true)}
            className="w-full"
            data-testid="team-role-change-btn"
          >
            Change role
          </PrimaryButton>
        </div>
      )}
      {confirming && (
        <div className="mt-3 space-y-2 rounded-md border border-amber-200 bg-amber-50/50 p-3">
          <p className="text-xs text-slate-800">
            Change role for <strong>{member.name}</strong>?
          </p>
          <div className="flex gap-2">
            <SecondaryButton
              type="button"
              className="flex-1"
              onClick={() => {
                setConfirming(false);
                setDraft(member.role);
              }}
              disabled={busy}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              className="flex-1"
              onClick={submit}
              disabled={busy}
              data-testid="team-role-confirm"
            >
              {busy ? "Saving…" : "Confirm"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// AccountActionsCard — owner only. Reset password, deactivate, reactivate.
// Deactivate uses typed-name confirmation (per locked decision).
// ---------------------------------------------------------------------------

function AccountActionsCard({ member, onSaved, onResetPassword }) {
  const [resetBusy, setResetBusy] = useState(false);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);
  const [reactivateBusy, setReactivateBusy] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [deactivateBusy, setDeactivateBusy] = useState(false);

  const reset = async () => {
    setResetBusy(true);
    try {
      const { temp_password } = await resetTeamMemberPassword(member.id);
      onResetPassword && onResetPassword(temp_password);
      toast.success("Password reset");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetBusy(false);
    }
  };

  const reactivate = async () => {
    setReactivateBusy(true);
    try {
      await reactivateTeamMember(member.id);
      toast.success(`${member.name} reactivated`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReactivateBusy(false);
    }
  };

  const deactivate = async () => {
    setDeactivateBusy(true);
    try {
      await deactivateTeamMember(member.id);
      toast.success(`${member.name} deactivated`);
      setConfirmingDeactivate(false);
      setTypedName("");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeactivateBusy(false);
    }
  };

  // Typed-name confirmation matches the RMA Danger Zone pattern.
  const expected = (member.name || "").trim().toLowerCase();
  const typedMatches = typedName.trim().toLowerCase() === expected && expected.length > 0;

  return (
    <section
      data-testid="team-account-actions"
      className="bg-white border border-slate-200 rounded-md p-4 space-y-3"
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
        Account actions
      </div>

      <SecondaryButton
        type="button"
        onClick={reset}
        disabled={resetBusy}
        className="w-full"
        data-testid="team-reset-password-btn"
      >
        <KeyIcon size={14} /> {resetBusy ? "Resetting…" : "Reset password"}
      </SecondaryButton>

      {member.is_active && (
        <div className="rounded-md border border-red-200 bg-red-50/40 p-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-red-800 font-bold mb-2">
            <WarningIcon size={14} /> Danger zone
          </div>
          {!confirmingDeactivate && (
            <DangerButton
              type="button"
              onClick={() => setConfirmingDeactivate(true)}
              className="w-full"
              data-testid="team-deactivate-btn"
            >
              Deactivate account
            </DangerButton>
          )}
          {confirmingDeactivate && (
            <div className="space-y-2">
              <p className="text-xs text-red-900">
                Type <code className="font-data text-[11px] bg-white border border-red-200 rounded px-1 py-0.5">{member.name}</code> to confirm. Deactivated users cannot log in.
              </p>
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={member.name}
                data-testid="team-deactivate-confirm-input"
                className={INPUT_CLS}
                autoFocus
              />
              <div className="flex gap-2">
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setConfirmingDeactivate(false);
                    setTypedName("");
                  }}
                  disabled={deactivateBusy}
                  className="flex-1"
                >
                  Cancel
                </SecondaryButton>
                <DangerButton
                  type="button"
                  onClick={deactivate}
                  disabled={deactivateBusy || !typedMatches}
                  className="flex-1"
                  data-testid="team-deactivate-confirm"
                >
                  {deactivateBusy ? "Working…" : "Deactivate"}
                </DangerButton>
              </div>
            </div>
          )}
        </div>
      )}

      {!member.is_active && (
        <PrimaryButton
          type="button"
          onClick={reactivate}
          disabled={reactivateBusy}
          className="w-full"
          data-testid="team-reactivate-btn"
        >
          {reactivateBusy ? "Reactivating…" : "Reactivate account"}
        </PrimaryButton>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Phase 11.1 — ChangePasswordCard (own profile only, all roles)
// ---------------------------------------------------------------------------

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [currentError, setCurrentError] = useState("");
  const [confirmTouched, setConfirmTouched] = useState(false);

  const allFilled = current.length > 0 && next.length > 0 && confirm.length > 0;
  const matches = next === confirm;
  const minLen = next.length >= 8;
  const showConfirmError = confirmTouched && confirm.length > 0 && !matches;
  const submitDisabled = !allFilled || !matches || !minLen || busy;

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setCurrentError("");
    setConfirmTouched(false);
  };

  const submit = async () => {
    setCurrentError("");
    setBusy(true);
    try {
      await changeOwnPassword({
        current_password: current,
        new_password: next,
      });
      reset();
      toast.success("Password updated successfully");
    } catch (e) {
      const msg = formatApiError(e);
      if (e?.response?.status === 422 && msg === "Current password is incorrect.") {
        setCurrentError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="team-change-password-card"
      className="bg-white border border-slate-200 rounded-md p-4 space-y-3"
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
        <LockKeyIcon size={14} /> Change password
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-1">
          Current password
        </label>
        <input
          type="password"
          value={current}
          onChange={(e) => {
            setCurrent(e.target.value);
            if (currentError) setCurrentError("");
          }}
          autoComplete="current-password"
          data-testid="team-change-password-current"
          className={INPUT_CLS}
        />
        {currentError && (
          <div
            data-testid="team-change-password-current-error"
            className="mt-1 text-[11px] text-red-700"
          >
            {currentError}
          </div>
        )}
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-1">
          New password
        </label>
        <input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          data-testid="team-change-password-new"
          className={INPUT_CLS}
        />
        <div className="mt-1 text-[11px] text-slate-500">
          Minimum 8 characters.
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-1">
          Confirm new password
        </label>
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          onBlur={() => setConfirmTouched(true)}
          autoComplete="new-password"
          data-testid="team-change-password-confirm"
          className={INPUT_CLS}
        />
        {showConfirmError && (
          <div
            data-testid="team-change-password-confirm-error"
            className="mt-1 text-[11px] text-red-700"
          >
            Passwords don't match.
          </div>
        )}
      </div>

      <PrimaryButton
        type="button"
        onClick={submit}
        disabled={submitDisabled}
        className="w-full"
        data-testid="team-change-password-submit"
      >
        {busy ? "Updating…" : "Update password"}
      </PrimaryButton>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function DetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-64 bg-slate-100 rounded" />
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-32 bg-slate-100 rounded-md border border-slate-200"
            />
          ))}
        </div>
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-28 bg-slate-100 rounded-md border border-slate-200"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
